import { TimezoneDTO, VenueDTO } from '@eventbrite/create-common';
import { sdkRequest } from '@eventbrite/http';
import { TransientAutoCreatedEvent } from '../../../shared/types';
import type { AsyncActionOptions, AutoCreateEventFormValues } from '../types';
import { createEventServicePayload } from './adapters';

export const createEvent = async ({
    currency,
    values,
    image,
    locale,
    organizationId,
    organizerId,
    options,
    venue,
    timezone,
    transientEvent,
}: {
    currency: string;
    values: AutoCreateEventFormValues;
    image: any;
    locale?: string;
    organizationId: string;
    organizerId: string;
    options?: AsyncActionOptions;
    venue?: VenueDTO;
    timezone: TimezoneDTO;
    transientEvent: TransientAutoCreatedEvent | null;
}) => {
    if (options?.testingFlags.shouldError) {
        await new Promise((resolve) => setTimeout(resolve, 1700));
        throw new Error('Error creating event');
    }

    const payload = createEventServicePayload({
        currency,
        values,
        timezone,
        locale,
        organizerId,
        venueId: venue?.id,
        venueName: venue?.name,
    });

    const createRequestOptions = {
        method: 'POST',
        body: JSON.stringify(payload),
    };

    const createBody = await sdkRequest(
        `/api/v3/organizations/${organizationId}/events/auto-create/`,
        createRequestOptions,
    );

    const event = createBody.event;

    if (transientEvent) {
        const updateRequestBody = {
            event: {
                summary: transientEvent.summary,
            } as any,
        };
        const updateStructuredContentBody = {
            modules: [
                {
                    data: {
                        body: {
                            alignment: 'left',
                            text: transientEvent.description,
                        },
                        type: 'text',
                    },
                    type: 'text',
                },
            ],
            publish: true,
            purpose: 'listing',
        } as any;

        if (image) {
            updateRequestBody.event.logo_id = image.id;
            updateStructuredContentBody.widgets = [
                {
                    data: {
                        slides: [
                            {
                                image: {
                                    image_id: image.id,
                                    url: image.url,
                                    original: image.original,
                                    crop_mask: image.crop_mask,
                                },
                            },
                        ],
                    },
                    type: 'herocarousel',
                },
            ];
        }

        const saveImageAndDescription = async () => {
            const versionBody = await sdkRequest(
                `/api/v3/events/${event.id}/structured_content/edit/?purpose=listing`,
            );

            return sdkRequest(
                `/api/v3/events/${event.id}/structured_content/${versionBody.page_version_number}/`,
                {
                    method: 'POST',
                    body: JSON.stringify(updateStructuredContentBody),
                },
            );
        };

        await Promise.all([
            sdkRequest(`/api/v3/events/${event.id}/`, {
                method: 'POST',
                body: JSON.stringify(updateRequestBody),
            }),
            saveImageAndDescription(),
        ]);
    }

    return createBody;
};
